import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  TablePagination,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { KundenService } from '../services/kundenService';
import { Kunde, KundeCreateRequest, KundeUpdateRequest } from '../types/kunde';

const KundenPage: React.FC = () => {
  const [kunden, setKunden] = useState<Kunde[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingKunde, setEditingKunde] = useState<Kunde | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);

  // Form State
  const [formData, setFormData] = useState<KundeUpdateRequest>({
    kundenNummer: '',
    name: '',
    anschriftStrasse: '',
    anschriftHausnummer: '',
    anschriftAdresszusatz: '',
    anschriftPlz: '',
    anschriftOrt: '',
    anschriftLand: '',
    telefon: '',
    email: '',
    wirtschaftszweig: ''
  });

  useEffect(() => {
    loadKunden();
  }, [page, rowsPerPage]);

  const loadKunden = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await KundenService.getAlleKunden(page, rowsPerPage, 'id', 'asc');
      setKunden(response.content);
      setTotalElements(response.totalElements);
    } catch (err) {
      console.error('Fehler beim Laden der Kunden:', err);
      setError('Fehler beim Laden der Kunden');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (kunde?: Kunde) => {
    if (kunde) {
      setEditingKunde(kunde);
      setFormData({
        kundenNummer: kunde.kundenNummer,
        name: kunde.name,
        anschriftStrasse: kunde.anschriftStrasse || '',
        anschriftHausnummer: kunde.anschriftHausnummer || '',
        anschriftAdresszusatz: kunde.anschriftAdresszusatz || '',
        anschriftPlz: kunde.anschriftPlz || '',
        anschriftOrt: kunde.anschriftOrt || '',
        anschriftLand: kunde.anschriftLand || '',
        telefon: kunde.telefon || '',
        email: kunde.email || '',
        wirtschaftszweig: kunde.wirtschaftszweig || ''
      });
    } else {
      setEditingKunde(null);
      setFormData({
        kundenNummer: '',
        name: '',
        anschriftStrasse: '',
        anschriftHausnummer: '',
        anschriftAdresszusatz: '',
        anschriftPlz: '',
        anschriftOrt: '',
        anschriftLand: '',
        telefon: '',
        email: '',
        wirtschaftszweig: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingKunde(null);
    setFormData({
      kundenNummer: '',
      name: '',
      anschriftStrasse: '',
      anschriftHausnummer: '',
      anschriftAdresszusatz: '',
      anschriftPlz: '',
      anschriftOrt: '',
      anschriftLand: '',
      telefon: '',
      email: '',
      wirtschaftszweig: ''
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingKunde) {
        // Update existing kunde - preserve registriertSeit and aktiv
        const updateData: KundeUpdateRequest = {
          ...formData,
          // Preserve existing system fields (not shown in dialog)
          registriertSeit: editingKunde.registriertSeit,
          aktiv: editingKunde.aktiv
        };
        await KundenService.updateKunde(editingKunde.id, updateData);
      } else {
        // Create new kunde - ensure required fields are present
        const createData: KundeCreateRequest = {
          kundenNummer: formData.kundenNummer!,
          name: formData.name!
        };
        await KundenService.createKunde(createData);
      }
      
      handleCloseDialog();
      loadKunden();
    } catch (err) {
      console.error('Fehler beim Speichern des Kunden:', err);
      setError('Fehler beim Speichern des Kunden');
    }
  };

  const handleDelete = async (kunde: Kunde) => {
    if (window.confirm(`Möchten Sie den Kunden "${kunde.name}" wirklich löschen?`)) {
      try {
        await KundenService.deleteKunde(kunde.id);
        loadKunden();
      } catch (err) {
        console.error('Fehler beim Löschen des Kunden:', err);
        setError('Fehler beim Löschen des Kunden');
      }
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Kunden-Verwaltung
        </Typography>
        <Box>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadKunden}
            sx={{ mr: 1 }}
          >
            Aktualisieren
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Neuer Kunde
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper elevation={3}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Kundennummer</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Anschrift</TableCell>
                <TableCell>Kontakt</TableCell>
                <TableCell>Erstellt am</TableCell>
                <TableCell align="center">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : kunden.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Keine Kunden gefunden
                  </TableCell>
                </TableRow>
              ) : (
                kunden.map((kunde) => (
                  <TableRow key={kunde.id} hover>
                    <TableCell>
                      <Chip label={kunde.id} size="small" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {kunde.kundenNummer}
                      </Typography>
                    </TableCell>
                    <TableCell>{kunde.name}</TableCell>
                    <TableCell>
                      {kunde.anschriftStrasse ? (
                        <>
                          <Typography variant="body2">
                            {kunde.anschriftStrasse} {kunde.anschriftHausnummer}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {kunde.anschriftPlz} {kunde.anschriftOrt}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          Keine Anschrift
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {kunde.telefon && (
                        <Typography variant="body2">
                          {kunde.telefon}
                        </Typography>
                      )}
                      {kunde.email && (
                        <Typography variant="body2" color="textSecondary">
                          {kunde.email}
                        </Typography>
                      )}
                      {!kunde.telefon && !kunde.email && (
                        <Typography variant="body2" color="textSecondary">
                          Keine Kontaktdaten
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{formatDate(kunde.erstelltAm)}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(kunde)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(kunde)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={totalElements}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Zeilen pro Seite:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} von ${count !== -1 ? count : `mehr als ${to}`}`
          }
        />
      </Paper>

      {/* Dialog für Kunde erstellen/bearbeiten */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingKunde ? 'Kunde bearbeiten' : 'Neuer Kunde'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  autoFocus
                  label="Kundennummer"
                  fullWidth
                  variant="outlined"
                  value={formData.kundenNummer}
                  onChange={(e) => setFormData({ ...formData, kundenNummer: e.target.value })}
                  autoComplete="off"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Name"
                  fullWidth
                  variant="outlined"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  autoComplete="off"
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                  Anschrift
                </Typography>
              </Grid>
              
              <Grid item xs={8}>
                <TextField
                  label="Straße"
                  fullWidth
                  variant="outlined"
                  value={formData.anschriftStrasse}
                  onChange={(e) => setFormData({ ...formData, anschriftStrasse: e.target.value })}
                  autoComplete="off"
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Hausnummer"
                  fullWidth
                  variant="outlined"
                  value={formData.anschriftHausnummer}
                  onChange={(e) => setFormData({ ...formData, anschriftHausnummer: e.target.value })}
                  autoComplete="off"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Adresszusatz"
                  fullWidth
                  variant="outlined"
                  value={formData.anschriftAdresszusatz}
                  onChange={(e) => setFormData({ ...formData, anschriftAdresszusatz: e.target.value })}
                  helperText="Optional (z.B. Hinterhaus, 2. Stock)"
                  autoComplete="off"
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="PLZ"
                  fullWidth
                  variant="outlined"
                  value={formData.anschriftPlz}
                  onChange={(e) => setFormData({ ...formData, anschriftPlz: e.target.value })}
                  autoComplete="off"
                />
              </Grid>
              <Grid item xs={8}>
                <TextField
                  label="Ort"
                  fullWidth
                  variant="outlined"
                  value={formData.anschriftOrt}
                  onChange={(e) => setFormData({ ...formData, anschriftOrt: e.target.value })}
                  autoComplete="off"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Land"
                  fullWidth
                  variant="outlined"
                  value={formData.anschriftLand}
                  onChange={(e) => setFormData({ ...formData, anschriftLand: e.target.value })}
                  helperText="ISO-Code (z.B. D, A, CH)"
                  autoComplete="off"
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                  Kontaktdaten
                </Typography>
              </Grid>
              
              <Grid item xs={6}>
                <TextField
                  label="Telefon"
                  fullWidth
                  variant="outlined"
                  value={formData.telefon}
                  onChange={(e) => setFormData({ ...formData, telefon: e.target.value })}
                  autoComplete="off"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="E-Mail"
                  type="email"
                  fullWidth
                  variant="outlined"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  autoComplete="off"
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                  Sonstiges
                </Typography>
              </Grid>
              
              <Grid item xs={4}>
                <TextField
                  label="Wirtschaftszweig"
                  fullWidth
                  variant="outlined"
                  value={formData.wirtschaftszweig}
                  onChange={(e) => setFormData({ ...formData, wirtschaftszweig: e.target.value })}
                  inputProps={{ maxLength: 3 }}
                  helperText="3-stelliger Code (z.B. 451)"
                  autoComplete="off"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Abbrechen
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={!formData.kundenNummer || !formData.name}
          >
            {editingKunde ? 'Aktualisieren' : 'Erstellen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KundenPage;
